import React, { useEffect, useMemo, useState } from 'react'
import { fmt } from '../api.js'
import { portal } from '../portalApi.js'
import VendorPickerModal from './VendorPickerModal.jsx'

// 거래 한 건의 분개를 쪼개는 편집기.
// 대부분은 계정과목 하나로 끝나서 초안 2줄 그대로면 되고, 외화 송금 수수료나
// 선급금/외상매입금 분할처럼 나눠 쳐야 하는 건만 줄을 늘린다.
// 저장하면 그 거래는 초안 대신 저장한 분개를 쓰고, 전부 지우면 초안으로 되돌아간다.
const emptyLine = (side) => ({ 차대: side, 계정코드: '', 계정명: '', 거래처코드: '', 거래처명: '', 금액: 0, 적요: '' })

export default function JournalEditor({ kind = 'bank', ticketId, onClose, onSaved }) {
  const [data, setData] = useState(null)
  const [lines, setLines] = useState([])
  const [cats, setCats] = useState([])
  const [err, setErr] = useState('')
  const [saving, setSaving] = useState(false)
  const [picking, setPicking] = useState(null) // 거래처 코드도움을 띄운 줄 index

  useEffect(() => {
    portal.get(`/api/journal/draft?kind=${kind}&ticketId=${ticketId}`)
      .then((d) => { setData(d); setLines(d.lines.map((l) => ({ ...l }))) })
      .catch((e) => setErr(e.message))
    portal.get('/api/fa/meta/categories').then(setCats).catch(() => {})
  }, [kind, ticketId])

  const catGroups = useMemo(() => {
    const groups = []
    let cur = null
    for (const c of cats) {
      const label = c.minor ? `${c.major} > ${c.minor}` : c.major
      if (!cur || cur.label !== label) { cur = { label, items: [] }; groups.push(cur) }
      cur.items.push(c)
    }
    return groups
  }, [cats])

  const debit = lines.filter((l) => l.차대 === '차').reduce((s, l) => s + (Number(l.금액) || 0), 0)
  const credit = lines.filter((l) => l.차대 === '대').reduce((s, l) => s + (Number(l.금액) || 0), 0)
  const balanced = Math.round(debit) === Math.round(credit)

  const patch = (i, p) => setLines((ls) => ls.map((l, n) => (n === i ? { ...l, ...p } : l)))
  const removeLine = (i) => setLines((ls) => ls.filter((_, n) => n !== i))

  // 외화 송금은 수수료가 출금 총액 안에 섞여 나온다 — 한 번에 갈라 준다
  const applyFeeSplit = () => {
    const s = data?.수수료제안
    if (!s) return
    const note = data.거래?.적요 || ''
    setLines([
      { ...emptyLine('차'), 계정코드: '131', 계정명: '선급금', 금액: s.송금원화, 적요: note },
      { ...emptyLine('차'), 계정코드: '831', 계정명: '지급수수료', 금액: s.수수료원화, 적요: `송금수수료 ${fmt(s.수수료외화, 2)} ${s.통화}` },
      { ...emptyLine('대'), 계정코드: '104', 계정명: '외화예금', 금액: s.총액원화, 적요: note },
    ])
  }

  const save = async () => {
    setSaving(true); setErr('')
    try {
      await portal.post('/api/journal/lines', { kind, ticketId, lines })
      onSaved?.(); onClose()
    } catch (e) { setErr(e.message) } finally { setSaving(false) }
  }

  const reset = async () => {
    setSaving(true); setErr('')
    try {
      await portal.post('/api/journal/lines', { kind, ticketId, lines: [] })
      onSaved?.(); onClose()
    } catch (e) { setErr(e.message) } finally { setSaving(false) }
  }

  const t = data?.거래 || {}

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div className="modal-panel journal-editor" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>분개 {data?.편집됨 ? '(직접 지정됨)' : '(자동 초안)'}</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>

        {!data && !err && <div className="muted" style={{ padding: 18 }}>불러오는 중…</div>}
        {err && <div className="error-box" style={{ margin: 18 }}>{err}</div>}

        {data && (
          <div className="modal-table-wrap" style={{ padding: 18 }}>
            <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
              {t.일자} · {t.우리계좌} · {t.적요 || t.상대입출금자명}
              <br />
              {Number(t.출금액) > 0 ? '출금' : '입금'} {fmt(Number(t.출금액) || Number(t.입금액))}원
              {t.외화여부 && ` (${fmt(t['출금액(외화)'] || t['입금액(외화)'], 2)} ${t.통화} @${fmt(t.적용환율, 2)})`}
            </div>

            {data.수수료제안 && (
              <div className="hint" style={{ marginBottom: 12 }}>
                외화 송금 건입니다 — 총액 {fmt(data.수수료제안.총액외화, 2)} {data.수수료제안.통화} 안에
                수수료 {fmt(data.수수료제안.수수료외화, 2)} {data.수수료제안.통화}
                ({fmt(data.수수료제안.수수료원화)}원)가 섞여 있습니다.
                <button type="button" className="btn secondary" style={{ marginLeft: 10 }} onClick={applyFeeSplit}>
                  송금액 + 수수료로 나누기
                </button>
              </div>
            )}

            <table className="modal-table">
              <thead>
                <tr>
                  <th style={{ width: 54 }}>차대</th>
                  <th>계정과목</th>
                  <th style={{ width: 150 }}>거래처</th>
                  <th style={{ width: 130 }}>금액</th>
                  <th>적요</th>
                  <th style={{ width: 34 }} />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td>
                      <select value={l.차대} onChange={(e) => patch(i, { 차대: e.target.value })}>
                        <option value="차">차</option><option value="대">대</option>
                      </select>
                    </td>
                    <td>
                      <select value={l.계정코드 || ''} style={{ maxWidth: 210 }}
                        onChange={(e) => {
                          const c = cats.find((x) => x.code === e.target.value)
                          patch(i, { 계정코드: c ? c.code : '', 계정명: c ? c.name : '' })
                        }}>
                        <option value="">미지정</option>
                        {catGroups.map((g) => (
                          <optgroup key={g.label} label={g.label}>
                            {g.items.map((x) => <option key={x.code} value={x.code}>{x.code} {x.name}</option>)}
                          </optgroup>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button type="button" className="link-cell" onClick={() => setPicking(i)}>
                        {l.거래처코드 ? `${l.거래처코드} ${l.거래처명}` : '지정'}
                      </button>
                    </td>
                    <td>
                      <input type="number" value={l.금액 ?? 0} style={{ width: 120, textAlign: 'right' }}
                        onChange={(e) => patch(i, { 금액: Number(e.target.value) })} />
                    </td>
                    <td>
                      <input className="wide" value={l.적요 || ''} onChange={(e) => patch(i, { 적요: e.target.value })} />
                    </td>
                    <td>
                      <button type="button" className="btn danger" onClick={() => removeLine(i)}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total">
                  <td colSpan={3} className="l">합계</td>
                  <td style={{ color: balanced ? undefined : 'var(--danger)' }}>
                    차 {fmt(debit)} / 대 {fmt(credit)}
                  </td>
                  <td colSpan={2} className="l" style={{ color: balanced ? 'var(--accent)' : 'var(--danger)' }}>
                    {balanced ? '대차 일치' : `차액 ${fmt(Math.abs(debit - credit))}`}
                  </td>
                </tr>
              </tfoot>
            </table>

            <div className="row" style={{ marginTop: 10 }}>
              <button type="button" className="btn secondary" onClick={() => setLines((ls) => [...ls, emptyLine('차')])}>+ 차변 줄</button>
              <button type="button" className="btn secondary" onClick={() => setLines((ls) => [...ls, emptyLine('대')])}>+ 대변 줄</button>
            </div>
          </div>
        )}

        <div className="modal-footer">
          {data?.편집됨 && (
            <button type="button" className="btn secondary" disabled={saving} onClick={reset}>
              자동 초안으로 되돌리기
            </button>
          )}
          <button type="button" className="btn secondary" onClick={onClose}>취소</button>
          <button type="button" className="btn" disabled={!data || saving || !balanced} onClick={save}>
            {saving ? '저장 중…' : '저장'}
          </button>
        </div>

        {picking !== null && (
          <VendorPickerModal
            initialQuery={lines[picking]?.거래처명 || ''}
            onSelect={(v) => { patch(picking, { 거래처코드: v.code, 거래처명: v.name }); setPicking(null) }}
            onClose={() => setPicking(null)}
          />
        )}
      </div>
    </div>
  )
}
